import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  TablePagination,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { assessmentService } from '@/services/assessment/assessmentService';
import type { AttemptListItem } from '@/types/assessment';

const AttemptsHistoryPage: React.FC = () => {
  const [attempts, setAttempts] = useState<AttemptListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  const fetchAttempts = async (page: number = 1, limit: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getMyAttempts({
        page,
        limit,
      });

      if (response.success && response.data) {
        setAttempts(response.data.attempts);
        setCurrentPage(response.data.pagination.currentPage);
        setTotalAttempts(response.data.pagination.totalAttempts);
      } else {
        setError('Failed to fetch attempts');
      }
    } catch (err) {
      setError('Error loading attempts');
      console.error('Error fetching attempts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setCurrentPage(newPage + 1); // TablePagination uses 0-based indexing, but our API uses 1-based
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handleViewDetails = (attemptId: string) => {
    navigate(`/attempts/${attemptId}`);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'abandoned':
        return 'error';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'in_progress':
        return <ScheduleIcon />;
      case 'abandoned':
      case 'expired':
        return <CancelIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      // bgcolor: 'background.default'
    }}>
      {/* Hero Header Section */}
      <Box
        sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
          // borderBottom: '1px solid',
          // borderBottomColor: 'divider',
          px: 3,
          py: 4,
          borderRadius:2
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3,
                boxShadow: 3,
              }}
            >
              <AssessmentIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                My Attempts History
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Track your assessment progress and performance
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/assessment')}
            sx={{ 
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Back to Assessment
          </Button>
        </Box>
        
        {/* Quick Stats */}
        {totalAttempts > 0 && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  p: 2.5,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2
                  }
                }}
              >
                <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {totalAttempts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Attempts
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  p: 2.5,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'success.main',
                    boxShadow: 2
                  }
                }}
              >
                <Typography variant="h5" color="success.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {attempts.filter(a => a.score.isPassed).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Passed
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  p: 2.5,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'warning.main',
                    boxShadow: 2
                  }
                }}
              >
                <Typography variant="h5" color="warning.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {attempts.length > 0 ? Math.round(attempts.reduce((acc, a) => acc + a.score.percentage, 0) / attempts.length) : 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Score
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>

      <Box sx={{ p: 3 }}>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {attempts?.length === 0 ? (
        <Card 
          sx={{ 
            borderRadius: 4,
            border: '2px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              No attempts yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Start your learning journey by taking your first assessment. Track your progress and improve your skills.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<StarIcon />}
              onClick={() => navigate('/assessment')}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                boxShadow: 3 
              }}
            >
              Take Your First Assessment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {attempts?.map((attempt) => (
              <Grid size={{xs: 12, md: 6, xl: 4}} key={attempt._id}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    transition: 'all 0.2s ease',
                    height: '100%',
                    '&:hover': { 
                      boxShadow: 3,
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header with Status */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box flex={1} mr={1}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: 0.5,
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {attempt.assessment.title}
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={attempt.assessment.difficulty}
                            color={attempt.assessment.difficulty === 'Beginner' ? 'success' : 
                                   attempt.assessment.difficulty === 'Intermediate' ? 'warning' : 'error'}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <Chip
                            size="small"
                            label={`#${attempt.attemptNumber}`}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      </Box>
                      <Chip
                        size="small"
                        icon={getStatusIcon(attempt.status)}
                        label={attempt.status === 'completed' ? 'Done' : attempt.status.toUpperCase()}
                        color={getStatusColor(attempt.status)}
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 24,
                          '& .MuiChip-icon': { fontSize: 14 }
                        }}
                      />
                    </Box>

                    {/* Score Section - Compact */}
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 2,
                        bgcolor: (theme) => attempt.score.isPassed ? 
                          theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.08)' :
                          theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.08)',
                        border: '1px solid',
                        borderColor: attempt.score.isPassed ? 'success.main' : 'error.main',
                        mb: 2
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          Score
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            color: attempt.score.isPassed ? 'success.main' : 'error.main'
                          }}
                        >
                          {attempt.score.percentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={attempt.score.percentage}
                        sx={{ 
                          height: 4, 
                          borderRadius: 2,
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                        }}
                        color={attempt.score.isPassed ? 'success' : 'error'}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {attempt.score.correctAnswers}/{attempt.score.totalQuestions} correct • {formatDuration(attempt.timing.totalTimeSpent)}
                      </Typography>
                    </Box>

                    {/* Date and Action */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(attempt.timing.startedAt)}
                        </Typography>
                        {attempt.timing.completedAt && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Completed
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(attempt._id)}
                        sx={{ 
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        View
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalAttempts > 0 && (
            <TablePagination
              component="div"
              count={totalAttempts}
              page={currentPage - 1} // Convert to 0-based indexing for TablePagination
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 15, 25, 50]}
              labelRowsPerPage="Attempts per page:"
              showFirstButton
              showLastButton
              sx={{ mt: 2, borderTop: '1px solid', borderTopColor: 'divider' }}
            />
          )}
        </>
      )}
      </Box>
    </Box>
  );
};

export default AttemptsHistoryPage;